import { Component, NgZone, OnInit, Renderer2 } from '@angular/core';
import { AddEvent, GridComponent, SaveEvent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { RaciService } from '../../services/raci.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Handles the participants assignment component
 */
@Component({
  selector: 'participants-assignment',
  templateUrl: './participants-assignment.component.html',
  styleUrls: ['./participants-assignment.component.css']
})
export class ParticipantsAssignmentComponent implements OnInit {
  /**
 * The project object
 */
  project: any;

  /**
   * The project roles array
   */
  projectRoles: any[];

  /**
   * The users object
   */
  users: any;

  /**
   * The participants object
   */
  participants: any;

  /**
   * The drop index
   */
  dropIndex: any;

  /**
   * The current grid reference
   */
  currentGridRef: GridComponent;

  /**
   * Array to store subscriptions
   */
  private subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
  * The mode for selection (single or multiple)
  */
  mode: SelectableMode = "single";

  /**
   * Flag indicating whether dragging is enabled
   */
  drag = false;

  /**
   * Flag indicating whether to show the external grid
   */
  showExternalGrid = false;

  /**
   * Settings for selectable items
   */
  selectableSettings: SelectableSettings;

  /**
   * The search term for filtering users
   */
  searchTerm = "";

  /**
   * Array to store filtered users
   */
  filteredUsers: any[];

  /**
   * Flag indicating whether to show the "No Records" info message
   */
  showNoRecordsInfoMessage = false;

  /**
   * Array to store filtered roles
   */
  filteredRoles: any[];

  /**
   * Array to store selected roles
   */
  selectedRoles = [];

  /**
   * Array to store filtered participants
   */
  filteredParticipants: any[];

  /**
   * Object for organization role list
   */
  organizationRoleList: any;

  /**
   * Object for sector list
   */
  sectorList: any;

  /**
   * Object for region list
   */
  regionList: any;

  /**
   * Array to store directors
   */
  directors: any[];

  /**
   * Array to store external users
   */
  externalUsers: any[];

  /**
   * Flag indicating whether to add external user
   */
  shouldAddExternalUser = false;

  /**
   * Array to store initial participant data
   */
  initialParticipantData = [];

  /**
   * Flag indicating whether the other users section is collapsed
   */
  otherUsersSectionCollapsed = true;

  /**
   * Flag indicating whether the directors section is collapsed
   */
  directorsSectionCollapsed = true;

  /**
   * Flag indicating whether the external section is collapsed
   */
  externalSectionCollapsed = true;

  /**
   * Flag indicating whether the unsaved changes dialog is enabled
   */
  isUnsavedChangesDialogEnabled = false;

  /**
   * Constructor
   * @param raciService Raci service
   * @param renderer Renderer
   * @param zone NgZone
   */
  constructor(private raciService: RaciService, private renderer: Renderer2, public zone: NgZone, private eventBus: EventBusService,
    private appStorage: AppStorageService, private _raciNavigationService: RaciNavigationService) {
    this.setSelectableSettings();
  }

  /**
   * Set Selectable Settings
   */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
  * On init
  * */
  ngOnInit(): void {
    this.project = this.appStorage.getSelectedProject();
    this.initialParticipantData = [];
    this.mapParticipantData();
    this.subscribeToSaveParticipantsEvent();
    this.subscribeToCloseParticipantsAssignmentEvent();
  }

  /**
   * Subscription to close participants assignment event
   * */
  private subscribeToCloseParticipantsAssignmentEvent() {
    this.subscriptions.push(this._raciNavigationService._onClickCloseParticipantsAssignment.subscribe(data => {
      if (data) {
        this.changesEvent(this.participants?.length != this.initialParticipantData?.length && this.isUnsavedChangesDialogEnabled || !!this.participantChange() && this.isUnsavedChangesDialogEnabled);
      }
    }));
  }

  /**
   * Subscription to save participants event
   * */
  private subscribeToSaveParticipantsEvent() {
    this.subscriptions.push(this._raciNavigationService._onSaveProjectParticipants.subscribe(data => {
      const unnasignedRols = this.participants?.find(x => { return x.projectRoleId == null; })
      if (unnasignedRols != null) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'errorParticipants');
      } else {
        this.raciService.updateProjectParticipants(this.participants)?.subscribe(data => {
          if (data) {
            this.shouldAddExternalUser = false;
            this.eventBus.broadcastEvent('disableCancel', false);
            this.initialParticipantData = [];
            this.refreshData();
            this.isUnsavedChangesDialogEnabled = false;
          }
        });
      }
    }));
  }

  /**
   * Retrieves participant data
   * */
  private mapParticipantData() {
    this.subscriptions.push(this.getData().subscribe(data => {
      if (data) {
        this.processProjectRolesData(data[0]);
        this.processParticipantData(data[1]);
        this.users = data[2];
        this.filterUsersByBusinessUnit();
        this.processRefListData(data[3], data[4], data[5])
        this.processDirectorsData();
        this.processExternalUsersData();
        this.processOtherUsersData();
      }
    }));
  }

  /**
   * On destory
   * */
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => { subscription.unsubscribe() });
    this.destroyListeners();
  }

  /**
   * Adds drag listeners to grid rows
   * */
  public setDraggableRows(): void {
    const gridRows: Array<HTMLTableElement> = this.getAllRows();
    gridRows.forEach((item: HTMLElement) => {
      this.renderer.setAttribute(item, "draggable", "true");
      this.addDragListeners(item);
    });
  }

  /**
   *  Retieves all grid rows
   * */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".k-grid tr"));
  }

  /**
   * Adds drag event listeners
   * @param item Item to which the drag event listener is being set
   */
  public addDragListeners(item: any): void {
    item.addEventListener("dragstart", (e: DragEvent) => {
      const rowIndex: number = item.rowIndex;
      if (rowIndex === null) {
        return;
      }

      const gridId = item.closest('.kendo-white-grid').id;

      const newParticipant = this.createNewParticipant(this.retrieveDraggedUser(gridId, rowIndex));
      const dataItem = JSON.stringify(newParticipant);
      e.dataTransfer.setData("text/plain", dataItem);
    });
  }

  /**
   * Drag start action
   * @param e Event
   * @param grid Grid from which the item is being dragged
   */
  public onDragStart(e: DragEvent, grid: GridComponent): void {
    this.currentGridRef = grid;
    const draggedElement: string = (e.target as HTMLElement).innerText;
    if (draggedElement.includes("Name")) {
      e.preventDefault();
    }
  }

  /**
   * On drop action
   * @param e Event
   * @param dragStartGridRef Grid Component from which the item is being dragged
   */
  public onDrop(e: DragEvent, dragStartGridRef: GridComponent): void {
    // Prevent dropping row in the same Grid
    if (dragStartGridRef !== this.currentGridRef) {
      const data = e.dataTransfer.getData("text/plain");
      const droppedItem: any = JSON.parse(data);
      if (this.filteredParticipants.filter(participant => participant.userId == droppedItem.userId).length >= this.filteredRoles.length) return;
      this.isUnsavedChangesDialogEnabled = true;
      if (droppedItem.isExternal) {
        if (this.participants.find(participant => { return participant.userId == droppedItem.userId; })) return;

        const externalProjectRole = this.getExternalRole();
        droppedItem.projectRoleId = externalProjectRole.oid;
        droppedItem.projectRole = externalProjectRole.description;
      }

      droppedItem.id = this.participants.length;
      this.participants.push(droppedItem);
      this.addDraggableRows();
      this.filterParticipantsBySelectedRoles(this.selectedRoles);
    }
  }

  /**
   * Adds draggable rows to the grid
   * */
  private addDraggableRows() {
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      this.destroyListeners();
      this.setDraggableRows();
    });
  }

  /**
   * Sets dorp index
   * @param e Event
   * @param grid Kedno grid
   */
  public onDragOver(e: DragEvent, grid: GridComponent): void {
    e.preventDefault();
    this.dropIndex = this.participants.length + 1;
  }

  /**
   * Removes row from grid
   * @param participant Item that is being remove from grid
   * @param droppedRowGridRef  Row that is being removed
   */
  public removeRow(participant: any, droppedRowGridRef: GridComponent): void {
    const droppedRowGridData = droppedRowGridRef.data as Array<any>
    const index: number = droppedRowGridData.findIndex(i => i.id === participant.id);
    const deleteParticipant = this.participants.find(user => { return user.id == participant.id });
    const participantIndex = this.participants.indexOf(deleteParticipant);
    this.isUnsavedChangesDialogEnabled = true;
    this.participants.splice(participantIndex, 1)
    this.checkForUnsavedExternalUsers();
    if (this.selectedRoles && this.selectedRoles.length > 0) {
      droppedRowGridData.splice(index, 1);
    }
  }

  /**
  * Creates new participant from user data
  * */
  private createNewParticipant(user: any): any {
    return {
      "name": user?.User_Description,
      "userId": user ? user.OID : -1,
      "email": user?.User_Email,
      "projectId": this.project.Oid,
      "projectRole": this.filteredRoles.length == 1 ? this.filteredRoles[0].description : null,
      "projectRoleId": this.filteredRoles.length == 1 ? this.filteredRoles[0].oid : null,
      "isExternal": user ? user.IsExternal : true,
      "organizationalRole": this.organizationRoleList.find(organizationRole => organizationRole.oid == user?.OrganizationalRoleId)?.description,
      "region": this.regionList.find(region => region.oid == user?.RegionId)?.description,
      "sector": this.sectorList.find(sector => sector.oid == user?.SectorId)?.description
    };
  }

  /**
   * Retrieves data
   * */
  private getData() {
    const roles = this.raciService.getProjectRoles();
    const participants = this.raciService.getParticipants(true, null, this.project.Oid);
    const users = this.raciService.getUsers(true, true);
    const organizationRoleList = this.raciService.loadReflist('ref_organizational_role');
    const sectorList = this.raciService.loadReflist('ref_sector');
    const regionList = this.raciService.loadReflist('ref_region');

    return forkJoin([roles, participants, users, organizationRoleList, sectorList, regionList]);
  }

  /**
   * Destroys drag start event listeners
   * */
  private destroyListeners(): void {
    const tableRows: Array<HTMLElement> = this.getAllRows();
    tableRows.forEach((item) => {
      item.removeEventListener("dragstart", () => {
        //Nothing
      });
    });
  }

  /**
   * Filter user by business units
   */
  filterUsersByBusinessUnit() {
    if (this.project.OwnerBuIdDescription != null && this.project.OwnerBuIdDescription != '') {
      this.users = this.users.filter(user => user.BusinessUnits.find(bu => bu.displayName == this.project.OwnerBuIdDescription))
    }
  }

  /**
   * Filter by selected role
   * @param event Event
   * @param role Selecter role
   */
  public filterByRole(event: any, role: any) {
    if (!event.currentTarget.classList.contains("participants-list-role-name-selected"))
      event.currentTarget.classList.add("participants-list-role-name-selected");
    else event.currentTarget.classList.remove("participants-list-role-name-selected")

    if (this.selectedRoles.find(x => x.description == role.description))
      this.selectedRoles.splice(this.selectedRoles.findIndex(selectedRole => selectedRole.description == role.description), 1);
    else
      this.selectedRoles.push(this.createRole(role));

    if (this.selectedRoles.length == 1 && this.selectedRoles[0].description.includes('external'))
      this.showExternalGrid = true;
    else
      this.showExternalGrid = false;

    this.filteredRoles = this.selectedRoles;
    this.filterParticipantsBySelectedRoles(this.selectedRoles);
  }

  /**
   * Filter participants by selected roles
   * @param selectedRoles Selected roles
   */
  filterParticipantsBySelectedRoles(selectedRoles: any[]) {
    if (selectedRoles.length == 0) {
      this.filteredParticipants = this.participants;
      this.filteredRoles = this.projectRoles;
    }
    else if (this.showExternalGrid) {
      this.filteredParticipants = this.participants.filter(participant =>
        this.selectedRoles.find(selectedRole => selectedRole.oid == participant.projectRoleId));
    }
    else {
      this.filteredParticipants = this.participants.filter(participant =>
        this.selectedRoles.find(selectedRole =>
          selectedRole.oid == participant.projectRoleId || participant.projectRoleId == null
        ));
    }
  }

  /**
   * Search user by name and last name
   * */
  searchUsers() {
    const directorInfo = this.organizationRoleList.find(role => role.description == "Director");

    this.filteredUsers = this.users.filter(user =>
      user.User_Description.toLowerCase().includes(this.searchTerm.toLowerCase()) &&
      user.OrganizationalRoleId != directorInfo.oid &&
      !user.IsExternal);

    this.directors = this.users.filter(user =>
      user.User_Description.toLowerCase().includes(this.searchTerm.toLowerCase()) &&
      user.OrganizationalRoleId == directorInfo.oid);

    this.externalUsers = this.users.filter(user => user.User_Description.toLowerCase().includes(this.searchTerm.toLowerCase()) && user.IsExternal);

    this.addDraggableRows();
    this.showNoRecordsInfoMessage = this.filteredUsers.length < 1 && this.directors.length < 1 && this.externalUsers.length < 1;
  }


  /**
   * Expands or collpses specified section
   * @param event Click event
   * @param section Section that was clicked
   */
  expandSection(event: any, section: any) {
    if (!event.currentTarget.classList.contains("icon-toogle")) {
      event.currentTarget.classList.remove("icon-toogle-rotate")
      event.currentTarget.classList.add("icon-toogle");
      this.expandOrCollapseSection(section, false);

    }
    else {
      event.currentTarget.classList.remove("icon-toogle")
      event.currentTarget.classList.add("icon-toogle-rotate");
      this.expandOrCollapseSection(section, true);
    }
    this.addDraggableRows();
  }

  /**
   * Expands or collapses specific section
   * @param section Section that needs to be expanded/collapsed
   * @param expand Flag indicating weather to collapse or expand a section
   */
  private expandOrCollapseSection(section: string, expand: boolean) {
    switch (section) {
      case 'other':
        this.otherUsersSectionCollapsed = expand;
        break;
      case 'directors':
        this.directorsSectionCollapsed = expand;
        break;
      case 'external':
        this.externalSectionCollapsed = expand;
        break;
    }
  }

  /**
   * Add new external user in external user grid
   * */
  public addExternalUser(args: AddEvent): void {
    args.sender.addRow(this.createNewParticipant(null));
  }

  /**
   * Save external user
   * */
  public saveExternalUser({ sender, rowIndex, formGroup }: SaveEvent): void {
    const participant = formGroup.value;

    this.eventBus.broadcastEvent('disableCancel', true);
    this.raciService.saveExternalUser(this.createNewExternalUser(participant)).subscribe(data => {
      if (data) {
        participant.userId = parseInt(data);
        this.shouldAddExternalUser = true;
        this.participants.push(participant);
        this.filterParticipantsBySelectedRoles(this.selectedRoles);
      }
    });

    sender.closeRow(rowIndex);
  }

  /**
   * Create new external user
   * @param user User
   * */
  createNewExternalUser(user) {
    return {
      "OID": user.userId,
      "UserAcdName": user.name,
      "User_Email": user.email,
      "User_Firstname": user?.name?.split(" ")[0],
      "User_Lastname": user?.name?.split(" ")[1],
      "User_Description": user?.name,
      "User_Active": true,
      "BusinessUnits": [],
      "IsExternal": true
    }
  }

  /**
   * Retrive external project role
   * */
  getExternalRole() {
    return this.projectRoles.find(role => { return role.description.includes("external") });
  }

  /**
   * Create new control infomarion
   * @param user User
   * */
  createNewControlInformation(user: any): any {
    return {
      "userId": user.userId,
      "projectRoleId": user.projectRoleId
    };
  }

  /**
   * Check if participant infomarion is changed
   * */
  participantChange(): boolean {
    return this.participants?.find(participant => {
      const user = this.initialParticipantData.find(info => { return info.id == participant.id });
      if (!user || user.projectRoleId != participant.projectRoleId) {
        return participant;
      }
    });
  }

  /**
   * Emit change event
  */
  changesEvent(value: any) {
    this._raciNavigationService.onCloseParticipantsAssignment(value);
  }

  /**
   * Check for unsaved external users on removing participants
  */
  checkForUnsavedExternalUsers() {
    const unsavedExternalUsers = this.participants.find(participant => { return participant.userId == -1 });
    if (!unsavedExternalUsers) {
      this.shouldAddExternalUser = false;
      this.eventBus.broadcastEvent('disableCancel', false);
    }
  }

  /**
   * Adds ids to participants
  */
  processParticipantsIds(participants: any) {
    for (let i = 0; i < participants.length; i++) {
      participants[i].id = i;
    }
  }

  /**
  * Create new role
  * @param role Role
  */
  private createRole(role: any): any {
    return {
      "oid": role.oid,
      "description": role.description
    }
  }

  /**
   * Retrieves user data for participant creation
   * @param gridId HTML id of the grid from which the user is being dragged
   * @param index Row index indicating the index of the user in the reference list
   */
  private retrieveDraggedUser(gridId: string, index: number): any {
    if (gridId === "directors") {
      return this.directors[index];
    }
    else if (gridId === "users") {
      return this.filteredUsers[index];
    }
    return this.externalUsers[index];
  }

  /**
   * Process directors list data
   * */
  private processDirectorsData() {
    const directorInfo = this.organizationRoleList.find(role => role.description == "Director");
    this.directors = this.users.filter(x => x.OrganizationalRoleId == directorInfo.oid);
    this.addDraggableRows();
  }

  /**
   * Process external users list
   * */
  private processExternalUsersData() {
    this.externalUsers = this.users.filter(user => user.IsExternal);
    this.addDraggableRows();
  }

  /**
 * Process other users list
 * */
  private processOtherUsersData() {
    const directorInfo = this.organizationRoleList.find(role => role.description == "Director");
    this.filteredUsers = this.users.filter(user => !user.IsExternal && user.OrganizationalRoleId != directorInfo.oid);
    this.addDraggableRows();
  }

  /**
   * Process participants data
   * @param participants Participants list
   */
  private processParticipantData(participants: any) {
    this.participants = participants;
    this.filteredParticipants = participants;
    this.participants.forEach(participant => this.initialParticipantData.push(this.createNewControlInformation(participant)));
    this.processParticipantsIds(this.participants);
  }

  /**
   * Process project roles data
   * @param roles Roles list
   */
  private processProjectRolesData(roles: any) {
    this.projectRoles = roles;
    this.filteredRoles = roles;
  }

  /**
   * Process refence list data
   * @param orgRoles Organizational roles data
   * @param sectors Sectors data
   * @param regions Regions data
   */
  private processRefListData(orgRoles: any, sectors: any, regions: any) {
    this.organizationRoleList = orgRoles;
    this.sectorList = sectors;
    this.regionList = regions;
  }

  /**
   * Refresh participants data
   * */
  private refreshData() {
    this.selectedRoles = [];
    this.mapParticipantData();
  }

}
